import type { WorkSheet } from "xlsx";

// xlsx is heavy and admin-only, so it loads on demand (shared chunk with
// chartParser's loader — Vite dedupes the dynamic import).
type XlsxModule = typeof import("xlsx");
let xlsxModule: Promise<XlsxModule> | undefined;
function loadXlsx(): Promise<XlsxModule> {
  xlsxModule ??= import("xlsx");
  return xlsxModule;
}

// Parses a wide, multi-block industry workbook (e.g. the TMT export) into a
// normalized ingest payload. Each block starts at a `日期` (date) column; its
// value columns are classified by header keyword so the SAME parser handles a
// different number of charts, different sigma bands, and different column counts
// across industries that follow the Wind header convention.

export type SeriesRole = "main" | "mean" | "band" | "right" | "threshold" | "extra";

export interface SeriesDef {
  key: string;
  label: string;
  role: SeriesRole;
  axis: "left" | "right";
}

export interface ChartConfig {
  id: string;
  title: string;
  leftUnit?: string;
  rightUnit?: string;
  series: SeriesDef[];
}

export type PointMap = Record<string, Record<string, Record<string, number>>>;

export interface ParsedIndustry {
  charts: ChartConfig[];
  points: PointMap;
  weeks: number;
}

// A1-notation encoder (replaces XLSX.utils.encode_cell so cellText doesn't
// need the lazily-loaded module).
function cellRef(r: number, c: number): string {
  let s = "";
  let n = c + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s + (r + 1);
}

function cellText(ws: WorkSheet, r: number, c: number): string {
  const cell = ws[cellRef(r, c)] as { w?: unknown; v?: unknown } | undefined;
  if (!cell) return "";
  return String(cell.w ?? cell.v ?? "").trim();
}

function parseNum(raw: string): number | null {
  if (!raw) return null;
  const t = raw.replace(/[%,\s]/g, "");
  if (t === "" || t === "-") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function normalizeDate(raw: string): string | null {
  if (!raw) return null;
  const m = raw.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  const serial = Number(raw);
  if (Number.isFinite(serial) && serial > 30000 && serial < 90000) {
    return new Date(Math.round((serial - 25569) * 86400 * 1000)).toISOString().slice(0, 10);
  }
  return null;
}

function cleanLabel(h: string): string {
  return h.replace(/[（(](左轴|右轴)[）)]/g, "").replace(/\s+/g, " ").trim();
}

function unitOf(h: string): string {
  if (h.includes("亿元")) return "亿元";
  if (h.includes("%") || h.includes("占比") || h.includes("分位") || h.includes("换手率")) return "%";
  return "";
}

function classify(h: string): { role: SeriesRole; sigma?: number } {
  if (h.includes("右轴")) return { role: "right" };
  if (h.includes("标准差")) {
    const m = h.match(/([\d.]+)\s*\*?\s*标准差/);
    return { role: "band", sigma: m ? Number(m[1]) : 1 };
  }
  if (h.includes("阈值")) return { role: "threshold" };
  if (h.includes("均值")) return { role: "mean" };
  return { role: "main" };
}

export async function parseIndustryWorkbook(buffer: ArrayBuffer): Promise<ParsedIndustry> {
  const XLSX = await loadXlsx();
  const wb = XLSX.read(new Uint8Array(buffer), { type: "array", cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws["!ref"]) return { charts: [], points: {}, weeks: 0 };
  const range = XLSX.utils.decode_range(ws["!ref"]);

  // Header row = the row (within the first few) holding the most "日期" cells.
  let headerRow = 0;
  let best = -1;
  // Scan well past the first rows: some exports place the metadata block and the
  // 日期 header lower down (e.g. row 16), so a tiny window would miss it.
  for (let r = 0; r <= Math.min(40, range.e.r); r++) {
    let count = 0;
    for (let c = 0; c <= range.e.c; c++) if (cellText(ws, r, c) === "日期") count++;
    if (count > best) {
      best = count;
      headerRow = r;
    }
  }

  const dateCols: number[] = [];
  for (let c = 0; c <= range.e.c; c++) if (cellText(ws, headerRow, c) === "日期") dateCols.push(c);

  const charts: ChartConfig[] = [];
  const points: PointMap = {};
  const allDates = new Set<string>();

  dateCols.forEach((dateCol, blockIndex) => {
    const endCol = blockIndex + 1 < dateCols.length ? dateCols[blockIndex + 1] : range.e.c + 1;

    // Classify every value column in the block.
    const classified: Array<{ col: number; header: string; role: SeriesRole; sigma?: number }> = [];
    for (let c = dateCol + 1; c < endCol; c++) {
      const h = cellText(ws, headerRow, c);
      if (h) classified.push({ col: c, header: h, ...classify(h) });
    }

    // The metric the bands describe is the left value column immediately before
    // the first mean/band column. Everything else on the left (raw inputs, the
    // metric's percentile twin, thresholds) is on a different scale, so for a
    // readable chart we keep only: main + mean + bands + the right-axis index.
    const stats = classified.filter((x) => x.role === "mean" || x.role === "band");
    const firstStat = stats.length ? Math.min(...stats.map((x) => x.col)) : Infinity;
    const mains = classified.filter((x) => x.role === "main");
    const before = mains.filter((x) => x.col < firstStat);
    const mainCol = before.length ? before[before.length - 1] : mains[0];
    if (!mainCol) return; // no primary metric -> not a chart

    const series: SeriesDef[] = [];
    const cols: Array<{ key: string; col: number }> = [];
    series.push({ key: "main", label: cleanLabel(mainCol.header), role: "main", axis: "left" });
    cols.push({ key: "main", col: mainCol.col });

    let meanDone = false;
    const bandSeq: Record<string, number> = {};
    for (const x of classified) {
      if (x.role === "mean" && !meanDone) {
        meanDone = true;
        series.push({ key: "mean", label: "평균", role: "mean", axis: "left" });
        cols.push({ key: "mean", col: x.col });
      } else if (x.role === "band") {
        const sg = x.sigma ?? 1;
        const tag = String(sg).replace(".", "_");
        bandSeq[tag] = bandSeq[tag] ?? 0;
        const key = `band_${tag}_${bandSeq[tag]++}`;
        series.push({ key, label: `±${sg}σ`, role: "band", axis: "left" });
        cols.push({ key, col: x.col });
      } else if (x.role === "right") {
        const n = series.filter((s) => s.role === "right").length;
        const key = n === 0 ? "right" : `right_${x.col}`;
        series.push({ key, label: cleanLabel(x.header), role: "right", axis: "right" });
        cols.push({ key, col: x.col });
      }
    }

    const chartId = `c${blockIndex}`;
    charts.push({
      id: chartId,
      title: cleanLabel(mainCol.header),
      leftUnit: unitOf(mainCol.header),
      rightUnit: "",
      series
    });

    const byDate: Record<string, Record<string, number>> = {};
    for (let r = headerRow + 1; r <= range.e.r; r++) {
      const date = normalizeDate(cellText(ws, r, dateCol));
      if (!date) continue;
      const row: Record<string, number> = {};
      for (const { key, col } of cols) {
        const v = parseNum(cellText(ws, r, col));
        if (v != null) row[key] = v;
      }
      if (Object.keys(row).length) {
        byDate[date] = row;
        allDates.add(date);
      }
    }
    points[chartId] = byDate;
  });

  return { charts, points, weeks: allDates.size };
}
